import { captureStuckSpinner } from '@/lib/sentry';

// See docs/superpowers/specs/2026-07-19-stuck-spinner-watchdog-design.md
export const TICK_MS = 3000;
export const THRESHOLD_MS = 15000;
export const ESCAPE_THRESHOLD_MS = 20000;
export const BLOCKING_VIEWPORT_RATIO = 0.25;
export const MAX_REPORTS_PER_SESSION = 5;
export const SPINNER_SELECTOR = '.animate-spin';
export const IGNORE_SELECTOR = '[data-stuck-watchdog="ignore"]';

const MAX_LABEL_LEN = 100;

/**
 * Best-effort human label for a spinner element: its (or an ancestor's)
 * aria-label, else the nearest heading text walking up a few ancestors,
 * else "unknown". Combined with the route this identifies the stuck screen.
 */
export function deriveSpinnerLabel(el: Element): string {
  const labelled = el.closest('[aria-label]');
  const aria = labelled?.getAttribute('aria-label')?.trim();
  if (aria) return aria.slice(0, MAX_LABEL_LEN);

  let node: Element | null = el;
  for (let depth = 0; node && depth < 4; depth++) {
    const heading = node.querySelector('h1, h2, [role="heading"]');
    const text = heading?.textContent?.trim();
    if (text) return text.slice(0, MAX_LABEL_LEN);
    node = node.parentElement;
  }

  return 'unknown';
}

/**
 * A spinner is "blocking" when it fills a full-screen container (onboarding
 * pages / overlays) or covers a large share of the viewport - i.e. the user
 * can't do anything else. Small inline spinners (buttons, table cells) are not.
 */
export function isBlockingSpinner(el: Element): boolean {
  const FULLSCREEN_CLASSES = ['min-h-screen', 'h-screen', 'inset-0'];
  const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
  const viewportArea = vw * vh;

  let node: Element | null = el;
  for (let depth = 0; node && depth < 6; depth++) {
    // Tokenized class match (avoids substring false positives like inset-0.5).
    if (FULLSCREEN_CLASSES.some((c) => node!.classList?.contains(c))) return true;
    // Ratio must measure the ANCESTOR box, not the small spinner glyph, so a
    // small icon centered in a full-screen loader still counts as blocking.
    if (viewportArea > 0) {
      const rect = node.getBoundingClientRect();
      if ((rect.width * rect.height) / viewportArea >= BLOCKING_VIEWPORT_RATIO) return true;
    }
    node = node.parentElement;
  }
  return false;
}

export interface StuckInfo {
  route: string;
  label: string;
}
const stuckListeners = new Set<(info: StuckInfo) => void>();
const clearedListeners = new Set<() => void>();

/**
 * Subscribe to blocking-spinner escape events. `onStuck` fires once when a
 * blocking spinner crosses ESCAPE_THRESHOLD_MS; `onCleared` fires when a
 * previously-escalated spinner leaves the DOM. Returns an unsubscribe fn.
 */
export function onStuckSpinner(onStuck: (info: StuckInfo) => void, onCleared?: () => void): () => void {
  stuckListeners.add(onStuck);
  if (onCleared) clearedListeners.add(onCleared);
  return () => {
    stuckListeners.delete(onStuck);
    if (onCleared) clearedListeners.delete(onCleared);
  };
}

function notifyStuck(info: StuckInfo): void {
  for (const cb of stuckListeners) {
    try { cb(info); } catch { /* subscriber errors must not break the tick */ }
  }
}

function notifyCleared(): void {
  for (const cb of clearedListeners) {
    try { cb(); } catch { /* ignore */ }
  }
}

interface ElementState {
  visibleMs: number;
  reported: boolean;
  escalated: boolean;
}

export interface StuckSpinnerWatcher {
  tick(): void;
  start(): void;
  stop(): void;
}

export function createStuckSpinnerWatcher(): StuckSpinnerWatcher {
  const tracked = new Map<Element, ElementState>();
  let reports = 0;
  let escalatedCount = 0;
  let intervalId: ReturnType<typeof setInterval> | null = null;

  function tick(): void {
    const nodes = new Set<Element>(
      Array.from(document.querySelectorAll(SPINNER_SELECTOR))
    );

    // Drop spinners that have left the DOM (resolved / unmounted).
    for (const el of Array.from(tracked.keys())) {
      if (!nodes.has(el)) {
        if (tracked.get(el)?.escalated) {
          escalatedCount = Math.max(0, escalatedCount - 1);
          if (escalatedCount === 0) notifyCleared();
        }
        tracked.delete(el);
      }
    }

    const isVisible = document.visibilityState === 'visible';

    for (const el of nodes) {
      if (el.closest(IGNORE_SELECTOR)) continue;

      let state = tracked.get(el);
      if (!state) {
        state = { visibleMs: 0, reported: false, escalated: false };
        tracked.set(el, state);
      }

      // Accumulate only while the tab is visible: backgrounded tabs throttle
      // timers and users walk away - neither is a real stuck spinner.
      if (isVisible) state.visibleMs += TICK_MS;

      if (
        !state.reported &&
        state.visibleMs >= THRESHOLD_MS &&
        reports < MAX_REPORTS_PER_SESSION
      ) {
        captureStuckSpinner({
          route: window.location.pathname,
          spinnerLabel: deriveSpinnerLabel(el),
          elapsedMs: state.visibleMs,
          visibility: document.visibilityState,
        });
        state.reported = true;
        reports += 1;
      }

      if (
        !state.escalated &&
        state.visibleMs >= ESCAPE_THRESHOLD_MS &&
        isBlockingSpinner(el)
      ) {
        state.escalated = true;
        escalatedCount += 1;
        notifyStuck({ route: window.location.pathname, label: deriveSpinnerLabel(el) });
      }
    }
  }

  return {
    tick,
    start() {
      if (intervalId !== null) return;
      intervalId = setInterval(tick, TICK_MS);
    },
    stop() {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
      tracked.clear();
    },
  };
}

let singleton: StuckSpinnerWatcher | null = null;

/**
 * Start the app-wide stuck-spinner watcher exactly once. Safe to call before
 * Sentry finishes initializing: captureStuckSpinner no-ops until it is ready.
 */
export function initStuckSpinnerWatchdog(): void {
  if (singleton !== null) return;
  if (typeof document === 'undefined') return;
  singleton = createStuckSpinnerWatcher();
  singleton.start();
}
